import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiCheck, FiArrowRight, FiPhone } from 'react-icons/fi';
import { FaBolt, FaTachometerAlt, FaUsers, FaShieldAlt } from 'react-icons/fa';
import { sampleGolfCarts } from '../data/sampleGolfCarts';
import { vehicleService } from '../services/vehicleService';

const GolfCarts = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Live carts from Supabase
  const [liveCarts, setLiveCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        // Get available golf carts (excludes sold)
        const { data, error: fetchError } = await vehicleService.getAvailableVehiclesByType(['golf-cart', 'golf_cart', 'golfcart']);

        if (fetchError) {
          console.error('GolfCarts page: Error loading:', fetchError);
          if (!cancelled) {
            setError('temporarily_unavailable');
            setLiveCarts([]);
          }
          return;
        }

        const onlyCarts = (Array.isArray(data) ? data : []).filter((v) => {
          const t = (v.vehicleType || '').toLowerCase();
          const name = `${(v.make || '')} ${(v.model || '')}`.toLowerCase();
          const isCart = t === 'golf-cart' || t === 'golf_cart' || t === 'golfcart';
          const looksLikeTrailer = name.includes('trailer');
          return isCart && !looksLikeTrailer;
        });
        if (!cancelled) setLiveCarts(onlyCarts);
      } catch (err) {
        console.error('GolfCarts page: Exception:', err);
        if (!cancelled) {
          setError('temporarily_unavailable');
          setLiveCarts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build display list (live preferred, else sample)
  const displayCarts = useMemo(() => {
    const buildFromLive = (v) => ({
      id: v.id,
      isLive: true,
      condition: (v.condition || '').toLowerCase(),
      brand: v.make || 'Golf Cart',
      model: v.model || '',
      price: v.price || 0,
      image: (Array.isArray(v.images) && v.images[0]) || v.image || '/assets/assets/images/business-shop-sign.webp',
      year: v.year,
      passengers: v.seating || undefined,
      topSpeed: undefined,
      range: undefined,
      features: Array.isArray(v.features) ? v.features : [],
      vin: v.vin || v.serial_number || '',
      mileage: v.mileage || undefined,
    });
    const buildFromSample = (s) => ({
      id: s.id,
      isLive: false,
      condition: s.condition,
      brand: s.brand,
      model: s.model,
      price: s.price,
      image: s.image,
      year: s.year,
      passengers: s.passengers,
      topSpeed: s.topSpeed,
      range: s.range,
      features: s.features,
      vin: s.vin || '',
      mileage: s.mileage || undefined,
    });
    // Don't fall back to sample data - show empty if no live data
    const list = liveCarts.length ? liveCarts.map(buildFromLive) : [];
    // Apply filter
    return list.filter((cart) => {
      if (selectedCategory === 'all') return true;
      if (selectedCategory === 'pre-owned') return cart.condition === 'used' || cart.condition === 'pre-owned';
      return cart.condition === selectedCategory;
    });
  }, [liveCarts, selectedCategory]);

  if (error === 'temporarily_unavailable') {
    return (
      <div className="min-h-screen bg-gray-50 py-8 pt-24">
        <div className="container mx-auto px-4">
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg max-w-2xl mx-auto">
            <div className="text-6xl mb-6">🔧</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Golf Carts Temporarily Unavailable</h1>
            <p className="text-gray-600 mb-2">We're experiencing a brief technical issue with our inventory system.</p>
            <p className="text-gray-600 mb-8">Our golf carts are still available — give us a call!</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+18173822316"
                className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors text-lg"
              >
                📞 Call Us: (817) 382-2316
              </a>
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold transition-colors text-lg"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Golf Carts for Sale in Paradise, TX | Club Car, EZGO, Yamaha | Paradise-Performance</title>
        <meta name="description" content="Shop pre-owned golf carts in Paradise, TX. Club Car, EZGO, Yamaha models. Electric and gas powered. 2-8 passenger seating. Custom builds available. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/golf-carts" />
        <meta property="og:title" content="Golf Carts for Sale - Paradise-Performance" />
        <meta property="og:description" content="Pre-owned golf carts for sale. Electric and gas models. Custom builds available." />
        <meta property="og:url" content="https://paradise-performance.com/golf-carts" />
      </Helmet>

      {/* Hero Section - Simple and Clean */}
      <section className="bg-gray-900 text-white py-16 pt-24 lg:pt-36 md:pt-16 sm:pt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Golf Carts
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Quality Pre-Owned Models • Service & Customization Available
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#inventory"
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded font-semibold transition-colors"
              >
                Browse Inventory
              </a>
              <a
                href="tel:9406832335"
                className="px-6 py-3 border-2 border-white text-white rounded font-semibold hover:bg-white hover:text-gray-900 transition-colors flex items-center justify-center gap-2"
              >
                <FiPhone /> Call Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Simple Grid */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-10 text-gray-900">
            Why Choose Paradise-Performance?
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-14 h-14 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                <FaBolt className="text-white text-xl" />
              </div>
              <h3 className="font-semibold mb-1">Fully Electric</h3>
              <p className="text-sm text-gray-600">Zero emissions, quiet operation</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                <FaShieldAlt className="text-white text-xl" />
              </div>
              <h3 className="font-semibold mb-1">Full Lighting</h3>
              <p className="text-sm text-gray-600">LED lights, turn signals & more</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                <FaUsers className="text-white text-xl" />
              </div>
              <h3 className="font-semibold mb-1">Expert Service</h3>
              <p className="text-sm text-gray-600">Professional maintenance team</p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                <FaTachometerAlt className="text-white text-xl" />
              </div>
              <h3 className="font-semibold mb-1">Performance</h3>
              <p className="text-sm text-gray-600">Top speed & range options</p>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Buttons */}
      <section id="inventory" className="py-6 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex justify-center gap-3">
            {['all', 'pre-owned'].map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded font-medium transition-colors ${selectedCategory === category
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {category === 'all' ? 'All Carts' : 'Pre-Owned'}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Golf Cart Grid - Clean Cards */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayCarts.map((cart) => (
              <div
                key={cart.id}
                className="bg-white rounded-lg shadow-sm hover:shadow-lg transition-shadow overflow-hidden"
              >
                {/* Image */}
                <div className="relative">
                  <img
                    src={cart.image}
                    alt={`${cart.brand} ${cart.model}`}
                    className="w-full h-56 object-cover"
                  />
                  {cart.condition && (
                    <span className={`absolute top-3 left-3 px-3 py-1 rounded text-white text-sm font-medium ${'bg-blue-600'
                      }`}>
                      {cart.condition.toUpperCase()}
                    </span>
                  )}
                  <span className="absolute top-3 right-3 bg-white px-3 py-1 rounded font-bold text-lg">
                    ${cart.price.toLocaleString()}
                  </span>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {cart.brand} {cart.model}
                  </h3>

                  <p className="text-sm text-gray-600 mb-2">
                    {[
                      cart.year ? String(cart.year) : null,
                      cart.passengers ? `${cart.passengers} Passengers` : null,
                      cart.mileage ? `${cart.mileage.toLocaleString()} mi` : null
                    ].filter(Boolean).join(' • ')}
                  </p>

                  {cart.vin && (
                    <p className="text-xs text-gray-500 font-mono truncate mb-3" title={cart.vin}>
                      VIN: {cart.vin}
                    </p>
                  )}

                  {/* Specs */}
                  {(cart.topSpeed || cart.range) && (
                    <div className="flex gap-4 text-sm text-gray-600 mb-3">
                      {cart.topSpeed && (
                        <span className="flex items-center gap-1">
                          <FaTachometerAlt className="text-primary-600" />
                          {cart.topSpeed}
                        </span>
                      )}
                      {cart.range && (
                        <span className="flex items-center gap-1">
                          <FaBolt className="text-primary-600" />
                          {cart.range}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Features */}
                  <div className="space-y-1 mb-4">
                    {cart.features.slice(0, 2).map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                        <FiCheck className="text-green-600 flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* View Button */}
                  <Link to={`/golf-cart/${cart.id}`}>
                    <button className="w-full bg-gray-900 text-white py-2 px-4 rounded font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-2">
                      View Details
                      <FiArrowRight />
                    </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Build CTA */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Want Something Custom?
          </h2>
          <p className="text-xl mb-8 text-primary-100 max-w-2xl mx-auto">
            We can build your dream golf cart with custom colors, lift kits, wheels, and more.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="px-6 py-3 bg-white text-primary-600 rounded font-semibold hover:bg-gray-100 transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
      {/* Policy Notice */}
      <div className="container mx-auto px-4 py-4 text-center">
        <p className="text-xs text-gray-500">
          All deposits are non-refundable. Please review our{' '}
          <Link to="/refund-policy" className="text-primary-600 hover:text-primary-700 underline">
            Refund & Return Policy
          </Link>{' '}
          before making a purchase.
        </p>
      </div>
    </div>
  );
};

export default GolfCarts;
